/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {ColDef} from 'ag-grid-community';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {
  Device,
  GenericObject,
  serviceStateType1
} from '@ion/core/src/types/entities';
import {sortByString} from '@ion/core/src/utils/ArrayUtil';
import {resolveDefinitions} from '@ion/dls/src/grid/ColumnResolver';
import {
  IonGrid,
  RowModelTypes
} from '@ion/dls/src/grid/IonGrid';
import {AG_CLASSES} from '@ion/dls/src/grid/IonGridUtils';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import {dlsLayout} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';

export interface ServiceTabType {
  device: Device;
  selectedTab: Number;
  tabIndex: Number;
};

enum ServiceType {
  ICN = 1,
  BLE = 3,
  IVT = 4,
  CARGO = 6,
  TPMS = 7,
  DOOR = 8,
  WETS = 9,
  BMS = 10,
  LMS = 13,
  WMS = 14,
  EA = 17,
  EA_STATUS = 18,
  STATUS_EA = 20
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  servicesGrid : {
    '& .ag-center-cols-viewport' : {
      overflowX : 'hidden !important'
    },
    '& .ag-popup' : {
      height : 'auto'
    },
    '&&' : {
      height                   : '100%',
      [AG_CLASSES.BODY_LAYOUT] : {
        overflowY : 'auto'
      }
    }
  },
  detailsTab : {
    height : '100%'
  },
  gridContainer : {
    height : 'calc(100vh - 304px)'
  }
}));

const pendingStatus    = {PendingEnable: 'Pending Enable', PendingDisable: 'Pending Disable'};
const nonPendingStatus = ['Enabled', 'Disabled'];

/**
 * Generic method to get the Service status
 * @param featureType Service type
 * @param features It contains all Service types with value
 * @returns service Status
 */
const getServiceStatus = (featureType, features): string => {
  if (features) {
    if (nonPendingStatus.includes(features[featureType])) {
      return features[featureType];
    }
    return pendingStatus[features[featureType]];
  }
  return '';
};

/**
 * ServicesTab component that displays the Device services
 * data for the selected Device Details tab.
 */
export const ServicesTab = ({
  device,
  tabIndex = 0,
  selectedTab = 0
}: ServiceTabType) => {
  const classes                               = useStyles();
  const [gridApi, setGridApi]                 = useState(null);
  const [hasItems, setHasItems]               = useState<boolean>(false);
  const [icnStatus, setICNStatus]             = useState<string>('');
  const [bleStatus, setBLEStatus]             = useState<string>('');
  const [ivtStatus, setIVTStatus]             = useState<string>('');
  const [cargoStatus, setCargoStatus]         = useState<string>('');
  const [doorStatus, setDoorStatus]           = useState<string>('');
  const [tpmsStatus, setTPMSStatus]           = useState<string>('');
  const [wetsStatus, setWETSStatus]           = useState<string>('');
  const [bmsStatus, setBMSStatus]             = useState<string>('');
  const [lmsStatus, setLMSStatus]             = useState<string>('');
  const [wmsStatus, setWMSStatus]             = useState<string>('');
  const [eaStatus, setEAStatus]               = useState<string>('');
  const [refreshRequired, setRefreshRequired] = useState<boolean>(false);
  const {servicesInfo}                        = useSelector(({devices: {view}}) => view);

  const {idReport} = device?.idReport?.device || {};
  const services   = idReport?.deviceServices?.services;
  const vasSummary = servicesInfo?.results[0]?.vasSummary;

  const {
    icn,
    ble,
    ivt,
    cargo,
    door,
    tpms,
    wets
  } = vasSummary || {};

  useEffect(() => {
    if (!isEmpty(servicesInfo?.results)) {
      setICNStatus(icn?.status);
      setBLEStatus(ble?.status);
      setIVTStatus(ivt?.status);
      setCargoStatus(cargo?.status);
      setDoorStatus(door?.status);
      setTPMSStatus(tpms?.status);
      setWETSStatus(wets?.status);
    }
  }, [servicesInfo]);

  useEffect(() => {
    services?.forEach(({state, typeId}) => {
      if (typeId === ServiceType.ICN) {
        setICNStatus(state ? serviceStateType1[state] : null);
      }
      if (typeId === ServiceType.BLE) {
        setBLEStatus(state ? serviceStateType1[state] : null);
      }
      if (typeId === ServiceType.IVT) {
        setIVTStatus(state ? serviceStateType1[state] : null);
      }
      if (typeId === ServiceType.CARGO) {
        setCargoStatus(state ? serviceStateType1[state] : null);
      }
      if (typeId === ServiceType.TPMS) {
        setTPMSStatus(state ? serviceStateType1[state] : null);
      }
      if (typeId === ServiceType.DOOR) {
        setDoorStatus(state ? serviceStateType1[state] : null);
      }
      if (typeId === ServiceType.WETS) {
        setWETSStatus(state ? serviceStateType1[state] : null);
      }
      if (typeId === ServiceType.BMS) {
        setBMSStatus(state ? serviceStateType1[state] : null);
      }
      if (typeId === ServiceType.LMS) {
        setLMSStatus(state ? serviceStateType1[state] : null);
      }
      if (typeId === ServiceType.WMS) {
        setWMSStatus(state ? serviceStateType1[state] : null);
      }
      if (typeId === ServiceType.EA || typeId === ServiceType.EA_STATUS || typeId === ServiceType.STATUS_EA) {
        setEAStatus(state ? serviceStateType1[state] : null);
      }
    });
  }, [services]);

  useEffect(() => {
    gridApi?.hideOverlay();
    if (gridApi && !hasItems) {
      gridApi.showNoRowsOverlay();
    }
  }, [hasItems]);

  const serviceListData = useMemo(() => ([
    {id: 1, serviceName: 'CrashBoxx', serviceStatus: getServiceStatus('ICN', device?.features), serviceSupport: icnStatus},
    {id: 2, serviceName: 'iOn Vision', serviceStatus: getServiceStatus('IVT', device?.features), serviceSupport: ivtStatus},
    {id: 3, serviceName: 'Tag Detection', serviceStatus: getServiceStatus('BLE', device?.features), serviceSupport: bleStatus},
    {id: 4, serviceName: 'Cargo Sensor', serviceStatus: getServiceStatus('CARGO', device?.features), serviceSupport: cargoStatus},
    {id: 5, serviceName: 'Door Event Sensor', serviceStatus: getServiceStatus('DOOR', device?.features), serviceSupport: doorStatus},
    {id: 6, serviceName: 'Tire Pressure Monitoring Sensor', serviceStatus: getServiceStatus('TPMS', device?.features), serviceSupport: tpmsStatus},
    {id: 7, serviceName: 'Wheel End Temperature Sensor', serviceStatus: getServiceStatus('WETS', device?.features), serviceSupport: wetsStatus},
    {id: 8, serviceName: 'Brake System Monitoring Service', serviceStatus: getServiceStatus('BMS', device?.features), serviceSupport: bmsStatus},
    {id: 9, serviceName: 'Lamp Monitoring Service', serviceStatus: getServiceStatus('LMS', device?.features), serviceSupport: lmsStatus},
    {id: 10, serviceName: 'Weight Monitoring Service', serviceStatus: getServiceStatus('WMS', device?.features), serviceSupport: wmsStatus},
    {id: 11, serviceName: 'Edge App', serviceStatus: getServiceStatus('EA', device?.features), serviceSupport: eaStatus}
  ]), [device, icnStatus, ivtStatus, bleStatus, cargoStatus, doorStatus, tpmsStatus, wetsStatus]);

  useEffect(() => {
    if (!services) {
      setRefreshRequired(true);
    }
  }, [serviceListData]);

  /**
   * Takes a list of services and constructs a list that
   * can be easily consumed by the IonGrid API
   */
  const constructServicesGridRows = useCallback(function(services: GenericObject[]): GridResult[] {
    const gridItems = services.map(service => ({
      ...service,
      serviceNameUI    : service.serviceName,
      serviceStatusUI  : service.serviceStatus,
      serviceSupportUI : service.serviceSupport
    })) as GridResult[];
    setHasItems(!!gridItems.length);
    return gridItems;
  }, []);

  // Provides reference to the grid's api.
  const onGridReady = useCallback(function(api) {
    setGridApi(api);
  }, []);

  const getGridHeight = useCallback(function() {
    return classes.servicesGrid;
  }, []);

  // memoize the sorted data set, only performing the sort when serviceListData changes ...
  const items = useMemo(() => (
    sortByString(serviceListData, 'serviceName')
  ), [serviceListData]);

  return (
    <div id={`detailsTab_${tabIndex}`} className={classes.detailsTab} hidden={selectedTab !== tabIndex}>
      <div className={classes.gridContainer}>
        <IonGrid omitHeader
          rowModelType={RowModelTypes.ClientSide}
          rowData={items}
          totalRows={serviceListData?.length}
          augmentItems={constructServicesGridRows}
          columnDefinitions={getColumnDefinitions()}
          refreshRequired={refreshRequired}
          setRefreshRequired={setRefreshRequired}
          checkboxesEnabled={false}
          showHeaderCheckbox={false}
          viewSubGroups={false}
          rowMenusEnabled={false}
          rowHeight={40}
          onGridReady={onGridReady}
          getGridHeight={getGridHeight}
          paginationEnabled
          showCustomPaginationBar
          pageOffset
          pageSizes={PAGE_SIZES} />
      </div>
    </div>
  );
};

/**
 * Returns column definitions for desktop Services grid
 */
const getColumnDefinitionsDesktop = (): ColDef[] => {
  return [{
    headerName : translate('admin-devices:view.services.serviceName'),
    field      : 'serviceNameUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.services.serviceStatus'),
    field      : 'serviceStatusUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.services.serviceSupport'),
    field      : 'serviceSupportUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  }];
};

/**
 * Returns column definitions for mobile Services grid
 */
const getColumnDefinitionsMobile = (): ColDef[] => {
  return [{
    headerName : translate('admin-devices:view.services.serviceName'),
    field      : 'serviceNameUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.services.serviceStatus'),
    field      : 'serviceStatusUI',
    sortable   : true,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  },
  {
    headerName : translate('admin-devices:view.services.serviceSupport'),
    field      : 'serviceSupportUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : true,
    hide       : false
  }];
};
const getColumnDefinitions = () => ({
  desktop : resolveDefinitions(getColumnDefinitionsDesktop()),
  mobile  : resolveDefinitions(getColumnDefinitionsMobile())
});
export default ServicesTab;
